import { z } from 'zod';

/**
 * Platform-agnostic keyboard shortcuts system
 * Designed to work across Electron, Chrome Extensions, and Web applications
 */

// Platform types
export const PlatformTypeSchema = z.enum(['electron', 'chrome-extension', 'web', 'ios', 'android']);
export type PlatformType = z.infer<typeof PlatformTypeSchema>;

// Shortcut scope - where the shortcut should be active
export const ShortcutScopeSchema = z.enum(['global', 'application', 'window', 'focused']);
export type ShortcutScope = z.infer<typeof ShortcutScopeSchema>;

// Key modifier schema
export const KeyModifierSchema = z.enum([
  'ctrl',
  'cmd',
  'alt',
  'shift',
  'meta',
  'super',
  'CommandOrControl', // Electron abstraction
  'CmdOrCtrl', // Alternative Electron abstraction
]);
export type KeyModifier = z.infer<typeof KeyModifierSchema>;

// Special keys schema
export const SpecialKeySchema = z.enum([
  'Enter',
  'Return',
  'Escape',
  'Space',
  'Tab',
  'Backspace',
  'Delete',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'F1',
  'F2',
  'F3',
  'F4',
  'F5',
  'F6',
  'F7',
  'F8',
  'F9',
  'F10',
  'F11',
  'F12',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'Insert',
]);
export type SpecialKey = z.infer<typeof SpecialKeySchema>;

// Key combination schema
export const KeyCombinationSchema = z.object({
  modifiers: z.array(KeyModifierSchema).default([]),
  key: z.string(), // Can be a letter, number, or SpecialKey
  scope: ShortcutScopeSchema.default('application'),
  description: z.string().optional(),
  category: z.string().optional(), // For grouping shortcuts (e.g., 'chat', 'capture', 'navigation')
});

export type KeyCombination = z.infer<typeof KeyCombinationSchema>;

// Platform-specific key representation
export const PlatformKeyRepresentationSchema = z.object({
  electron: z.string(), // e.g., "CommandOrControl+Enter"
  chromeExtension: z.string(), // e.g., "Ctrl+Enter" (Chrome extension format)
  web: z.string(), // e.g., "ctrl+enter" (Web KeyboardEvent format)
  display: z.string(), // Human-readable format e.g., "Ctrl + Enter"
});

export type PlatformKeyRepresentation = z.infer<typeof PlatformKeyRepresentationSchema>;

// Shortcut definition schema
export const ShortcutDefinitionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  category: z.string(),
  defaultKeys: KeyCombinationSchema,
  platformKeys: PlatformKeyRepresentationSchema.optional(),
  enabled: z.boolean().default(true),
  customizable: z.boolean().default(true),
  conflicts: z.array(z.string()).default([]), // IDs of conflicting shortcuts
  lastUsed: z.number().optional(),
});

export type ShortcutDefinition = z.infer<typeof ShortcutDefinitionSchema>;

// Shortcut action result
export const ShortcutActionResultSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
  preventDefault: z.boolean().default(true),
  stopPropagation: z.boolean().default(true),
});

export type ShortcutActionResult = z.infer<typeof ShortcutActionResultSchema>;

// Shortcut handler function type
export type ShortcutHandler = (
  context?: ShortcutContext
) => Promise<ShortcutActionResult> | ShortcutActionResult;

// Context passed to shortcut handlers
export const ShortcutContextSchema = z.object({
  platform: PlatformTypeSchema,
  timestamp: z.number(),
  originalEvent: z.any().optional(), // Platform-specific event object
  activeElement: z.string().optional(), // CSS selector or element identifier
  windowId: z.string().optional(),
  metadata: z.record(z.string(), z.any()).default({}),
});

export type ShortcutContext = z.infer<typeof ShortcutContextSchema>;

// Shortcut registry entry
export const ShortcutRegistryEntrySchema = z.object({
  definition: ShortcutDefinitionSchema,
  handler: z.function().args(z.any()).returns(z.any()), // Will be the actual function in implementation
  isRegistered: z.boolean().default(false),
  registrationError: z.string().optional(),
  usageCount: z.number().default(0),
  lastTriggered: z.number().optional(),
});

export type ShortcutRegistryEntry = z.infer<typeof ShortcutRegistryEntrySchema>;

// Shortcut manager state
export const ShortcutManagerStateSchema = z.object({
  platform: PlatformTypeSchema,
  isInitialized: z.boolean().default(false),
  shortcuts: z.record(z.string(), ShortcutRegistryEntrySchema).default({}),
  conflicts: z
    .array(
      z.object({
        shortcutIds: z.array(z.string()),
        keyString: z.string(),
        severity: z.enum(['warning', 'error']),
        resolved: z.boolean().default(false),
      })
    )
    .default([]),
  lastError: z.string().optional(),
  stats: z
    .object({
      totalShortcuts: z.number().default(0),
      activeShortcuts: z.number().default(0),
      totalTriggers: z.number().default(0),
      averageResponseTime: z.number().default(0),
    })
    .default({
      totalShortcuts: 0,
      activeShortcuts: 0,
      totalTriggers: 0,
      averageResponseTime: 0,
    }),
});

export type ShortcutManagerState = z.infer<typeof ShortcutManagerStateSchema>;

// Default shortcut definitions for the application
export const DEFAULT_SHORTCUTS: Record<string, ShortcutDefinition> = {
  'show-hide': {
    id: 'show-hide',
    name: 'Show/Hide App',
    description: 'Show or hide the application window',
    category: 'navigation',
    defaultKeys: {
      modifiers: ['CommandOrControl'],
      key: 'Enter',
      scope: 'global',
      description: 'Toggle app visibility',
    },
    platformKeys: {
      electron: 'CommandOrControl+Enter',
      chromeExtension: 'Ctrl+Enter',
      web: 'ctrl+enter',
      display: 'Ctrl + Enter',
    },
    enabled: true,
    customizable: true,
    conflicts: [],
  },
  'new-chat': {
    id: 'new-chat',
    name: 'New Chat',
    description: 'Start a new chat session when app is open',
    category: 'chat',
    defaultKeys: {
      modifiers: ['CommandOrControl'],
      key: 'n',
      scope: 'application',
      description: 'Create new chat session when app is focused',
    },
    platformKeys: {
      electron: 'CommandOrControl+N',
      chromeExtension: 'Ctrl+N',
      web: 'ctrl+n',
      display: 'Ctrl + N',
    },
    enabled: true,
    customizable: true,
    conflicts: [],
  },
  'close-overlay': {
    id: 'close-overlay',
    name: 'Close Overlay',
    description: 'Close the chat overlay',
    category: 'navigation',
    defaultKeys: {
      modifiers: [],
      key: 'Escape',
      scope: 'window',
      description: 'Close modal/overlay',
    },
    platformKeys: {
      electron: 'Escape',
      chromeExtension: 'Escape',
      web: 'escape',
      display: 'Escape',
    },
    enabled: true,
    customizable: false, // Standard behavior
    conflicts: [],
  },
  'send-message': {
    id: 'send-message',
    name: 'Send Message',
    description: 'Send the current message in chat',
    category: 'chat',
    defaultKeys: {
      modifiers: [],
      key: 'Enter',
      scope: 'focused',
      description: 'Send message when input is focused',
    },
    platformKeys: {
      electron: 'Enter',
      chromeExtension: 'Enter',
      web: 'enter',
      display: 'Enter',
    },
    enabled: true,
    customizable: true,
    conflicts: [],
  },
  'new-line': {
    id: 'new-line',
    name: 'New Line',
    description: 'Add a new line in the message input',
    category: 'chat',
    defaultKeys: {
      modifiers: ['shift'],
      key: 'Enter',
      scope: 'focused',
      description: 'Add line break in message',
    },
    platformKeys: {
      electron: 'Shift+Enter',
      chromeExtension: 'Shift+Enter',
      web: 'shift+enter',
      display: 'Shift + Enter',
    },
    enabled: true,
    customizable: true,
    conflicts: [],
  },
  'recapture-screen': {
    id: 'recapture-screen',
    name: 'Recapture Screen',
    description: 'Recapture the current screen in active chat session',
    category: 'capture',
    defaultKeys: {
      modifiers: ['CommandOrControl', 'shift'],
      key: 'c',
      scope: 'application',
      description: 'Recapture screen when chat is active',
    },
    platformKeys: {
      electron: 'CommandOrControl+Shift+C',
      chromeExtension: 'Ctrl+Shift+C',
      web: 'ctrl+shift+c',
      display: 'Ctrl + Shift + C',
    },
    enabled: true,
    customizable: true,
    conflicts: [],
  },
  'open-settings': {
    id: 'open-settings',
    name: 'Open Settings',
    description: 'Open application settings',
    category: 'navigation',
    defaultKeys: {
      modifiers: ['CommandOrControl'],
      key: ',',
      scope: 'application',
      description: 'Open settings panel',
    },
    platformKeys: {
      electron: 'CommandOrControl+,',
      chromeExtension: 'Ctrl+,',
      web: 'ctrl+,',
      display: 'Ctrl + ,',
    },
    enabled: true,
    customizable: true,
    conflicts: [],
  },
};

// Utility functions for key string conversion
export const keyToString = (combination: KeyCombination, platform: PlatformType): string => {
  const modifiers = combination.modifiers.join('+');
  const key = combination.key;

  switch (platform) {
    case 'electron':
      return modifiers ? `${modifiers}+${key}` : key;
    case 'chrome-extension':
      // Chrome extensions use specific format
      return modifiers.replace('CommandOrControl', 'Ctrl') + (modifiers ? '+' : '') + key;
    case 'web':
      return (modifiers ? `${modifiers.toLowerCase()}+` : '') + key.toLowerCase();
    default:
      return modifiers ? `${modifiers}+${key}` : key;
  }
};

export const stringToKey = (keyString: string): KeyCombination => {
  const parts = keyString.split('+');
  const key = parts.pop() || '';
  const modifiers = parts as KeyModifier[];

  return {
    modifiers,
    key,
    scope: 'application',
  };
};

// Validation functions
export const validateShortcutDefinition = (data: unknown): ShortcutDefinition => {
  return ShortcutDefinitionSchema.parse(data);
};

export const validateShortcutManagerState = (data: unknown): ShortcutManagerState => {
  return ShortcutManagerStateSchema.parse(data);
};

export const validateKeyCombination = (data: unknown): KeyCombination => {
  return KeyCombinationSchema.parse(data);
};
